import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChannelBadge, StatusBadge } from "@/components/Badges";
import { Plus, Trash2, Pencil, RefreshCw, AlertTriangle, FileText, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import TemplateBuilderDialog from "@/components/TemplateBuilderDialog";
import { useAuth } from "@/contexts/AuthContext";

const LOCAL_BLANK = { name: "", channel: "sms", body: "", category: "utility", status: "approved" };
const STATUS_STYLE = {
  APPROVED: "border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20",
  PENDING:  "border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-900/20",
  REJECTED: "border-red-300 text-red-700 bg-red-50 dark:bg-red-900/20",
  PAUSED:   "border-orange-300 text-orange-700 bg-orange-50 dark:bg-orange-900/20",
  DISABLED: "border-zinc-400 text-zinc-700 bg-zinc-100 dark:bg-zinc-900/20",
};

/* ─────────────────── Read-only WhatsApp template preview ─────────────────── */
function componentByType(components, type) {
  return (components || []).find(c => (c.type || "").toUpperCase() === type.toUpperCase()) || null;
}

function TemplatePreviewModal({ open, onOpenChange, template }) {
  if (!template) return null;
  const header = componentByType(template.components, "HEADER");
  const body = componentByType(template.components, "BODY");
  const footer = componentByType(template.components, "FOOTER");
  const buttonsBlock = componentByType(template.components, "BUTTONS");
  const status = String(template.status || "").toUpperCase();

  const headerFormat = header ? (header.format || "TEXT").toUpperCase() : "NONE";
  const headerText = header?.text || "";
  const headerMediaUrl =
    header?.example?.header_handle?.[0] ||
    header?.example?.header_url?.[0] ||
    header?.header_url ||
    "";
  const headerMediaFilename = header?.example?.header_filename || "attachment";

  const bodyText = body?.text || template.body_preview || "";
  const footerText = footer?.text || "";
  const buttons = (buttonsBlock?.buttons || []).map(b => ({
    type: (b.type || "").toUpperCase(),
    text: b.text || "",
    url: b.url || "",
    phone_number: b.phone_number || "",
  }));

  // Collect variables and their sample values (Meta stores them under `example`).
  const headerVars = header?.example?.header_text?.[0] ? [header.example.header_text[0]] : [];
  const bodyVarSamples = body?.example?.body_text?.[0] || [];
  const bodyVarCount = (bodyText.match(/\{\{(\d+)\}\}/g) || []).length;
  const headerVarCount = (headerText.match(/\{\{(\d+)\}\}/g) || []).length;

  // Render body with sample values substituted where available (purely visual).
  const previewBody = bodyText.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const idx = parseInt(n, 10) - 1;
    return bodyVarSamples[idx] || `{{${n}}}`;
  });
  const previewHeader = headerText.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const idx = parseInt(n, 10) - 1;
    return headerVars[idx] || `{{${n}}}`;
  });

  const STATUS_COLOR = {
    APPROVED: "border-emerald-300 text-emerald-700",
    PENDING:  "border-amber-300 text-amber-700",
    REJECTED: "border-red-300 text-red-700",
    PAUSED:   "border-orange-300 text-orange-700",
    DISABLED: "border-zinc-400 text-zinc-700",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wa-template-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4" /> Template Preview — <span className="font-mono">{template.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left column — structured metadata */}
          <div className="space-y-3 text-xs" data-testid="wa-template-preview-meta">
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 rounded-sm border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Status</div>
                <Badge variant="outline" className={`rounded-sm text-[10px] mt-0.5 ${STATUS_COLOR[status] || STATUS_COLOR.DISABLED}`}>{status || "—"}</Badge>
              </div>
              <div className="p-2 rounded-sm border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Category</div>
                <div className="font-mono">{(template.category || "—").toUpperCase()}</div>
              </div>
              <div className="p-2 rounded-sm border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Language</div>
                <div className="font-mono">{template.language || "—"}</div>
              </div>
              <div className="p-2 rounded-sm border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Header Type</div>
                <div className="font-mono">{headerFormat}</div>
              </div>
            </div>

            {(bodyVarCount + headerVarCount) > 0 && (
              <div className="p-2 rounded-sm border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Variables ({bodyVarCount + headerVarCount})</div>
                {headerVarCount > 0 && (
                  <div className="space-y-1 mb-1.5">
                    <div className="text-[10px] font-semibold text-muted-foreground">Header</div>
                    {Array.from({ length: headerVarCount }, (_, i) => (
                      <div key={`hv${i}`} className="font-mono text-[11px] flex justify-between gap-2">
                        <span>{`{{${i + 1}}}`}</span>
                        <span className="text-muted-foreground truncate">{headerVars[i] || "—"}</span>
                      </div>
                    ))}
                  </div>
                )}
                {bodyVarCount > 0 && (
                  <div className="space-y-1">
                    <div className="text-[10px] font-semibold text-muted-foreground">Body</div>
                    {Array.from({ length: bodyVarCount }, (_, i) => (
                      <div key={`bv${i}`} className="font-mono text-[11px] flex justify-between gap-2">
                        <span>{`{{${i + 1}}}`}</span>
                        <span className="text-muted-foreground truncate">{bodyVarSamples[i] || "—"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {buttons.length > 0 && (
              <div className="p-2 rounded-sm border">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Buttons ({buttons.length})</div>
                <div className="space-y-1">
                  {buttons.map((b, i) => (
                    <div key={i} className="text-[11px] flex justify-between gap-2">
                      <span className="font-mono text-muted-foreground">{b.type}</span>
                      <span className="truncate">{b.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column — WhatsApp-style mobile chat preview */}
          <div className="flex justify-center" data-testid="wa-template-preview-mobile">
            <div className="w-full max-w-[300px] rounded-xl border shadow-sm overflow-hidden" style={{ background: "#efe7dd" }}>
              {/* Chat header (WhatsApp green) */}
              <div className="flex items-center gap-2 px-3 py-2" style={{ background: "#075E54", color: "#fff" }}>
                <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-semibold">B</div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold truncate">Business</div>
                  <div className="text-[10px] opacity-80">online</div>
                </div>
              </div>

              {/* Message bubble */}
              <div className="p-3">
                <div className="ml-1 max-w-[92%] rounded-md p-2 text-[12px] whitespace-pre-wrap break-words" style={{ background: "#ffffff", boxShadow: "0 1px 0 rgba(0,0,0,0.08)" }}>
                  {/* Header */}
                  {headerFormat === "TEXT" && previewHeader && (
                    <div className="font-semibold text-[13px] mb-1">{previewHeader}</div>
                  )}
                  {headerFormat === "IMAGE" && (
                    headerMediaUrl ? (
                      <img src={headerMediaUrl} alt="header" className="w-full rounded-sm mb-1.5 border" style={{ maxHeight: 160, objectFit: "cover" }} />
                    ) : (
                      <div className="w-full h-24 mb-1.5 border rounded-sm bg-zinc-100 flex items-center justify-center text-[10px] text-muted-foreground">📷 Image header</div>
                    )
                  )}
                  {headerFormat === "VIDEO" && (
                    headerMediaUrl ? (
                      <video src={headerMediaUrl} controls className="w-full rounded-sm mb-1.5 border" style={{ maxHeight: 160 }} />
                    ) : (
                      <div className="w-full h-24 mb-1.5 border rounded-sm bg-zinc-100 flex items-center justify-center text-[10px] text-muted-foreground">🎬 Video header</div>
                    )
                  )}
                  {headerFormat === "DOCUMENT" && (
                    <div className="flex items-center gap-2 p-2 mb-1.5 border rounded-sm bg-zinc-50">
                      <FileText className="h-6 w-6 text-red-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-semibold truncate">{headerMediaFilename}</div>
                        <div className="text-[9px] text-muted-foreground">Document</div>
                      </div>
                      {headerMediaUrl && (
                        <a href={headerMediaUrl} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5">
                          View <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Body */}
                  <div className="text-[12px] leading-snug">{previewBody || "—"}</div>

                  {/* Footer */}
                  {footerText && (
                    <div className="text-[10px] text-muted-foreground mt-1.5">{footerText}</div>
                  )}

                  {/* Timestamp */}
                  <div className="text-[9px] text-muted-foreground text-right mt-1">12:34 PM</div>
                </div>

                {/* Buttons */}
                {buttons.length > 0 && (
                  <div className="ml-1 mt-1 space-y-0.5">
                    {buttons.map((b, i) => (
                      <div key={i} className="rounded-md text-[12px] py-1.5 text-center" style={{ background: "#ffffff", color: "#00A5F4", boxShadow: "0 1px 0 rgba(0,0,0,0.08)" }}>
                        {b.type === "URL" && "🔗 "}
                        {b.type === "PHONE_NUMBER" && "📞 "}
                        {b.type === "QUICK_REPLY" && "↩ "}
                        {b.text || (b.type === "URL" ? b.url : b.type === "PHONE_NUMBER" ? b.phone_number : "Reply")}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="text-[10px] text-muted-foreground text-center pt-2 border-t">
          Read-only preview. To edit or delete, use the actions on the template row.
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────── WhatsApp tab (Meta Graph API) ─────────────────── */
function WhatsAppTemplates({ canManage }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showBuilder, setShowBuilder] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/whatsapp/templates");
      if (data && data.ok === false) {
        setError(data.error || "Failed to load WhatsApp templates");
        setTemplates(data.templates || []);
      } else {
        setTemplates(Array.isArray(data) ? data : (data?.templates || []));
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to load WhatsApp templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (name) => {
    if (!window.confirm(`Delete template "${name}"? This is permanent on Meta and cannot be undone.`)) return;
    try {
      await api.delete(`/whatsapp/templates/${encodeURIComponent(name)}`);
      toast.success("Template deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const headerType = (t) => {
    const c = (t.components || []).find(x => (x.type || "").toUpperCase() === "HEADER");
    return c ? (c.format || "TEXT") : "—";
  };

  return (
    <div className="space-y-3" data-testid="wa-templates-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground">
          Templates fetched live from Meta Graph API. Only <strong>APPROVED</strong> templates can be sent to customers outside the 24-hour session window.
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-sm gap-1" data-testid="wa-templates-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Loading…" : "Refresh"}
          </Button>
          {canManage && (
            <Button type="button" size="sm" onClick={() => setShowBuilder(true)} className="rounded-sm gap-1" data-testid="wa-templates-create">
              <Plus className="h-3.5 w-3.5" /> Create Template
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-sm border border-red-300 bg-red-50 dark:bg-red-900/20 text-xs flex items-start gap-2" data-testid="wa-templates-error">
          <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-900 dark:text-red-200">Couldn&apos;t load templates from Meta</div>
            <div className="text-red-800 dark:text-red-300 mt-0.5">{error}</div>
            <div className="text-red-700 dark:text-red-300 mt-1">Connect your WhatsApp number under <a className="underline" href="/whatsapp-settings">WhatsApp Settings</a> first, then click Refresh.</div>
          </div>
        </div>
      )}

      {!error && !loading && templates.length === 0 && (
        <div className="text-xs text-muted-foreground p-6 border border-dashed rounded-sm text-center" data-testid="wa-templates-empty">
          No templates found for this WhatsApp Business Account.
          {canManage && (
            <div className="mt-2">
              <Button size="sm" variant="outline" onClick={() => setShowBuilder(true)} className="rounded-sm gap-1">
                <Plus className="h-3.5 w-3.5" /> Create your first template
              </Button>
            </div>
          )}
        </div>
      )}

      {templates.length > 0 && (
        <Card className="rounded-sm shadow-none">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="wa-templates-list">
              <thead className="bg-muted/40">
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Language</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Vars</th>
                  <th className="px-3 py-2">Header</th>
                  <th className="px-3 py-2">Body preview</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => {
                  const status = String(t.status || "").toUpperCase();
                  return (
                    <tr key={`${t.name}_${t.language}`} className="border-t border-border hover:bg-muted/20" data-testid={`wa-template-row-${t.name}`}>
                      <td className="px-3 py-2 font-mono">{t.name}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">{t.language}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="rounded-sm text-[10px]">{(t.category || "—").toUpperCase()}</Badge></td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS_STYLE[status] || STATUS_STYLE.DISABLED}`}>{status || "—"}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{t.variable_count ?? 0}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">{headerType(t)}</td>
                      <td className="px-3 py-2 max-w-xs truncate" title={t.body_preview}>{t.body_preview || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => setPreviewTpl(t)} data-testid={`wa-template-view-${t.name}`} title="Preview">
                            <Eye className="h-3.5 w-3.5 text-blue-600" />
                          </Button>
                          {canManage && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => del(t.name)} data-testid={`wa-template-delete-${t.name}`} title="Delete">
                              <Trash2 className="h-3.5 w-3.5 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <TemplateBuilderDialog open={showBuilder} onOpenChange={setShowBuilder} onCreated={load} />
      <TemplatePreviewModal open={!!previewTpl} onOpenChange={(v) => { if (!v) setPreviewTpl(null); }} template={previewTpl} />
    </div>
  );
}

/* ─────────────────── SMS / RCS tab (local templates) ─────────────────── */
function LocalTemplates({ channel, canManage }) {
  const [tpls, setTpls] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...LOCAL_BLANK, channel });

  const load = () => api.get("/templates").then(r => setTpls(r.data.filter(t => t.channel === channel)));
  useEffect(() => { load(); }, [channel]);

  const extractVars = (b) => Array.from(new Set([...b.matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1])));

  const openCreate = () => { setEditing(null); setForm({ ...LOCAL_BLANK, channel }); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, channel: t.channel, body: t.body, category: t.category || "utility", status: t.status || "approved" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, variables: extractVars(form.body) };
    try {
      if (editing) {
        await api.patch(`/templates/${editing.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/templates", payload);
        toast.success("Template created");
      }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete template?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-3" data-testid={`${channel}-templates-tab`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground">
          {channel === "sms"
            ? "SMS templates stored on-platform. DLT-approved headers configured under Providers."
            : "RCS message templates for rich business messaging on Android."}
        </div>
        {canManage && (
          <Button type="button" size="sm" onClick={openCreate} className="rounded-sm gap-1" data-testid={`${channel}-templates-create`}>
            <Plus className="h-3.5 w-3.5" /> Create Template
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {tpls.map(t => (
          <Card key={t.id} className="rounded-sm shadow-none" data-testid={`template-card-${t.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <ChannelBadge channel={t.channel} />
                <StatusBadge status={t.status} />
              </div>
              <div className="font-semibold">{t.name}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">{t.category}</div>
              <div className="text-sm p-3 rounded-sm bg-muted/40 border border-border font-mono whitespace-pre-wrap">{t.body}</div>
              {t.variables?.length > 0 && (
                <div className="text-xs"><span className="text-muted-foreground">Vars: </span>
                  <span className="font-mono">{t.variables.join(", ")}</span>
                </div>
              )}
              {canManage && (
                <div className="flex justify-end gap-1 pt-1">
                  <Button variant="ghost" size="sm" className="rounded-sm gap-1" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}>
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600 rounded-sm gap-1" onClick={() => del(t.id)} data-testid={`delete-template-${t.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {tpls.length === 0 && (
          <div className="text-xs text-muted-foreground p-6 border border-dashed rounded-sm text-center col-span-full">
            <FileText className="h-5 w-5 mx-auto mb-2 opacity-40" />
            No {channel.toUpperCase()} templates yet.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit ${channel.toUpperCase()} Template` : `Create ${channel.toUpperCase()} Template`}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="template-name-input" /></div>
              <div>
                <Label>Category</Label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="h-9 w-full px-3 rounded-sm border border-border bg-background text-sm">
                  <option value="utility">utility</option>
                  <option value="marketing">marketing</option>
                  <option value="authentication">authentication</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Body (use {`{{name}}`}, {`{{order_id}}`} etc)</Label>
              <Textarea required rows={4} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} className="rounded-sm" data-testid="template-body-input" />
              <div className="text-xs text-muted-foreground mt-1">Detected variables: {extractVars(form.body).join(", ") || "—"}</div>
            </div>
            <DialogFooter><Button type="submit" className="rounded-sm" data-testid="save-template-button">{editing ? "Save" : "Create"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────── Page shell ─────────────────── */
export default function Templates() {
  const { user } = useAuth();
  const [tab, setTab] = useState("sms");
  // Agents can view + send but cannot create/edit/delete templates
  const canManage = useMemo(() => ["super_admin", "admin", "manager"].includes(user?.role), [user?.role]);

  return (
    <div className="space-y-4" data-testid="templates-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Content</div>
          <h1 className="text-3xl font-black tracking-tighter">Templates</h1>
          <div className="text-xs text-muted-foreground mt-1">Manage message templates by channel. WhatsApp templates sync live with Meta Graph API.</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 rounded-sm max-w-md">
          <TabsTrigger value="sms" className="rounded-sm" data-testid="templates-tab-sms">SMS</TabsTrigger>
          <TabsTrigger value="whatsapp" className="rounded-sm" data-testid="templates-tab-whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="rcs" className="rounded-sm" data-testid="templates-tab-rcs">RCS</TabsTrigger>
        </TabsList>
        <TabsContent value="sms" className="mt-4"><LocalTemplates channel="sms" canManage={canManage} /></TabsContent>
        <TabsContent value="whatsapp" className="mt-4"><WhatsAppTemplates canManage={canManage} /></TabsContent>
        <TabsContent value="rcs" className="mt-4"><LocalTemplates channel="rcs" canManage={canManage} /></TabsContent>
      </Tabs>
    </div>
  );
}
